import { createStore } from "vuex";
import authService from "../services/authService";
import currencyExchangeService from "../services/currencyExchangeService";

export default createStore({
  state: {
    user: null,
    userProfile: null,
    isAuthenticated: false,
    loading: false,
    cars: [],
    customers: [],
    // Currency state
    exchangeRates: null,
    exchangeRatesUpdatedAt: null,
    preferredCurrency: "TRY",
  },
  getters: {
    isAuthenticated: (state) => state.isAuthenticated,
    currentUser: (state) => state.user,
    currentUserProfile: (state) => state.userProfile,
    isAdmin: (state) => state.userProfile?.role === "admin",
    userMoneyAmount: (state) => state.userProfile?.moneyAmount || 0,
    allCars: (state) => state.cars,
    allCustomers: (state) => state.customers,
    // Currency getters
    exchangeRates: (state) => state.exchangeRates,
    exchangeRatesUpdatedAt: (state) => state.exchangeRatesUpdatedAt,
    preferredCurrency: (state) => state.preferredCurrency,
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user;
      state.isAuthenticated = !!user;
    },
    SET_USER_PROFILE(state, profile) {
      state.userProfile = profile;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_CARS(state, cars) {
      state.cars = cars;
    },
    SET_CUSTOMERS(state, customers) {
      state.customers = customers;
    },
    UPDATE_USER_MONEY(state, amount) {
      if (state.userProfile) {
        state.userProfile.moneyAmount = amount;
      }
    },
    // Currency mutations
    SET_EXCHANGE_RATES(state, { rates, updatedAt }) {
      state.exchangeRates = rates;
      state.exchangeRatesUpdatedAt = updatedAt;
    },
    SET_PREFERRED_CURRENCY(state, currency) {
      state.preferredCurrency = currency;
      // Also update user profile if available
      if (state.userProfile) {
        state.userProfile.preferredCurrency = currency;
      }
    },
  },
  actions: {
    async initializeAuth({ commit }) {
      commit("SET_LOADING", true);

      return new Promise((resolve) => {
        const unsubscribe = authService.onAuthStateChange(
          async (user, profile) => {
            commit("SET_USER", user);
            commit("SET_USER_PROFILE", profile);

            // Set preferred currency from user profile
            if (profile && profile.preferredCurrency) {
              commit("SET_PREFERRED_CURRENCY", profile.preferredCurrency);
            }

            // Update exchange rates if needed (on login)
            if (user) {
              try {
                const ratesResult =
                  await currencyExchangeService.updateExchangeRatesIfNeeded();
                if (ratesResult.success) {
                  commit("SET_EXCHANGE_RATES", {
                    rates: ratesResult.rates,
                    updatedAt: ratesResult.updatedAt,
                  });
                }
              } catch (error) {
                console.error("Error updating exchange rates on login:", error);
              }
            }

            commit("SET_LOADING", false);
            unsubscribe();
            resolve({ user, profile });
          }
        );
      });
    },

    async login({ commit }, { email, password }) {
      commit("SET_LOADING", true);
      const result = await authService.login(email, password);
      commit("SET_LOADING", false);
      return result;
    },

    async register({ commit }, userData) {
      commit("SET_LOADING", true);
      const result = await authService.register(
        userData.email,
        userData.password,
        userData
      );
      commit("SET_LOADING", false);
      return result;
    },

    async logout({ commit }) {
      commit("SET_LOADING", true);
      const result = await authService.logout();
      if (result.success) {
        commit("SET_USER", null);
        commit("SET_USER_PROFILE", null);
      }
      commit("SET_LOADING", false);
      return result;
    },

    async resetPassword({ commit }, email) {
      commit("SET_LOADING", true);
      const result = await authService.resetPassword(email);
      commit("SET_LOADING", false);
      return result;
    },

    async updateUserMoney({ commit, state }, { amount, operation }) {
      if (state.user) {
        const result = await authService.updateUserMoney(
          state.user.uid,
          amount,
          operation
        );
        if (result.success) {
          commit("UPDATE_USER_MONEY", result.newAmount);
        }
        return result;
      }
      return { error: "No user logged in", success: false };
    },

    // Currency actions
    async updateExchangeRates({ commit }, force = false) {
      try {
        const result = force
          ? await currencyExchangeService.forceUpdateExchangeRates()
          : await currencyExchangeService.updateExchangeRatesIfNeeded();

        if (result.success) {
          commit("SET_EXCHANGE_RATES", {
            rates: result.rates,
            updatedAt: result.updatedAt,
          });
        }

        return result;
      } catch (error) {
        console.error("Error updating exchange rates:", error);
        return { success: false, error: error.message };
      }
    },

    async setPreferredCurrency({ commit, state }, currency) {
      try {
        commit("SET_PREFERRED_CURRENCY", currency);

        // Update user profile in Firebase if user is logged in
        if (state.user && state.userProfile) {
          const result = await authService.updateUserProfile(state.user.uid, {
            preferredCurrency: currency,
          });

          if (result.success) {
            // Update local profile
            commit("SET_USER_PROFILE", {
              ...state.userProfile,
              preferredCurrency: currency,
            });
          }

          return result;
        }

        return { success: true };
      } catch (error) {
        console.error("Error setting preferred currency:", error);
        return { success: false, error: error.message };
      }
    },
  },
  modules: {},
});
